import { AppThunk } from "..";
import { api } from "../../services/Api";
import {
  GetLocatorPayloadType,
  LocatorsDataTypes,
} from "../../types/LocatorTypes";
import {
  getActiveLocator,
  getLocatorData,
  getLocatorFetchStatus,
  getLocatorFilters,
  getLocatorUpdateStatus,
  getUploadedLocatorData,
} from "../selectors/LocatorSelectors";
import {
  setLocatorUpdateStatus,
  updateActiveLocator,
  updateAddLocatorStatus,
  updateLocatorData,
  updateLocatorFetchStatus,
  updateLocatorLengths,
  updateLocatorTotalAmount,
  updateLocatorTypes,
  updateLocatorUploadStatus,
  updateNewLocatorData,
  updateUploadedLocatorData,
} from "../reducers/LocatorsReducers";

const createUrlParams = (payload: unknown) =>
  payload
    ? "?" +
      Object.keys(payload)
        .filter((item) => payload[item as keyof typeof payload] !== "undefined")
        .map((key) => `${[key]}=${payload[key as keyof typeof payload]}`)
        .join("&")
    : "";

export const getLocatorsLengthsAction =
  (payload?: { locatorId: string }): AppThunk =>
  (dispatch, getState) => {
    const isLocatorFetching = getLocatorFetchStatus(getState()) === "loading";
    
    if (isLocatorFetching) return;
    
    dispatch(updateLocatorFetchStatus("loading"));

    api
      .get(`/locators/lengths${createUrlParams(payload)}`)
      .then((locatorResponse) => {
        dispatch(updateLocatorLengths(locatorResponse));
        dispatch(updateLocatorFetchStatus("success"));
      })
      .catch(() => {
        dispatch(updateLocatorFetchStatus("err"));
        dispatch(updateLocatorLengths(null));
      });
  };

export const  getLocatorsTypesAction =
  (payload?: { lengthId: string }): AppThunk =>
  (dispatch, getState) => {
    const isLocatorFetching = getLocatorFetchStatus(getState()) === "loading";

    if (isLocatorFetching) return;
    dispatch(updateLocatorFetchStatus("loading"));

    api
      .get(`/locators/types${createUrlParams(payload)}`)
      .then((locatorResponse) => {
        dispatch(updateLocatorTypes(locatorResponse));
        dispatch(updateLocatorFetchStatus("idle"));
      })
      .catch(() => {
        dispatch(updateLocatorFetchStatus("err"));
        dispatch(updateLocatorTypes(null));
      });
  };

export const createLocatorTypeAction =
  (payload?: { typename: string }): AppThunk =>
  (dispatch, getState) => {
    api
      .post('/locators/types', payload)
      .then((response: any) => {
        dispatch(getLocatorsTypesAction())
      })
      .catch((error: any) => {console.log(error)})
  }

export const createLocatorLengthAction =
  (payload?: { length: number}): AppThunk =>
  (dispatch, getState) => {
    api
      .post('/locators/lengths', payload)
      .then((response: any) => {
        dispatch(getLocatorsLengthsAction())
      })
      .catch((error: any) => {console.log(error)})
}


export const getLocatorsAction =
  (payload?: GetLocatorPayloadType): AppThunk =>
  (dispatch, getState) => {
    const isLocatorFetching = getLocatorFetchStatus(getState()) === "loading";

    if (isLocatorFetching) return;
    dispatch(updateLocatorFetchStatus("loading"));

    const locatorFilters = getLocatorFilters(getState());

    const validFilters = Object.keys(locatorFilters)
      .filter(
        (key) =>
          locatorFilters[key as keyof typeof locatorFilters] !== "undefined"
      )
      .map((item) => ({
        [item]: locatorFilters[item as keyof typeof locatorFilters],
      }))
      .flat()
      .reduce((acc, curr) => Object.assign(acc, curr), {});

    api
      .get(`/locators${createUrlParams({ ...payload, ...validFilters })}`)
      .then((locatorResponse) => {
        dispatch(updateLocatorData(locatorResponse));
        dispatch(updateLocatorFetchStatus("success"));
        setTimeout(() => {
          dispatch(updateLocatorFetchStatus("idle"));
        }, 100);
      })
      .catch(() => {
        dispatch(updateLocatorFetchStatus("error"));
        dispatch(updateLocatorData(null));
      });
  };

export const uploadLocatorAction =
  (locatorData: any): AppThunk =>
  (dispatch, getState) => {
    dispatch(updateLocatorUploadStatus("loading"));

    api
      .upload(`/upload-locator-image`, locatorData)
      .then(({ locatorid, path }) => {
        const currentLocatorData = getUploadedLocatorData(getState());
        dispatch(
          updateUploadedLocatorData({ ...currentLocatorData, locatorid, path })
        );
        dispatch(updateLocatorUploadStatus("success"));
      })
      .catch(() => {
        dispatch(updateLocatorUploadStatus("error"));
        dispatch(updateUploadedLocatorData(null));
      });
  };

export const addLocatorToDatasetAction =
  (payload: any): AppThunk =>
  (dispatch) => {
    dispatch(updateAddLocatorStatus("loading"));

    api
      .post("/add-locator-to-dataset", payload)
      .then((response) => {
        dispatch(updateNewLocatorData(response));
        dispatch(updateAddLocatorStatus("success"));
      })
      .catch(() => {
        dispatch(updateAddLocatorStatus("error"));
        dispatch(updateNewLocatorData(null));
      });
  };

export const updateExistingLocatorAction =
  (payload: LocatorsDataTypes): AppThunk =>
  (dispatch, getState) => {
    const isLocatorUpdating = getLocatorUpdateStatus(getState()) === "loading";

    if (isLocatorUpdating) return;
    const { locatorid, typeid, lengthid } = payload;
    dispatch(setLocatorUpdateStatus("loading"));

    api
      .put(`/locators/${locatorid}`, { locatorid, typeid, lengthid })
      .then((result) => {
        const currentLocatorData = getLocatorData(getState());

        if (!currentLocatorData) {
          dispatch(updateActiveLocator(result));
          dispatch(setLocatorUpdateStatus("success"));
          return;
        }

        // Cannot work with locator data directly
        const locatorDataCopy = [...currentLocatorData];

        const updatedIndex = currentLocatorData?.findIndex(
          (item) => item.locatorid === result.locatorid
        );

        locatorDataCopy[updatedIndex] = result;

        dispatch(updateLocatorData(locatorDataCopy));
        dispatch(updateActiveLocator(result));
        dispatch(setLocatorUpdateStatus("success"));
      })
      .catch(() => {
        dispatch(setLocatorUpdateStatus("error"));
      });
  };

export const deleteLocatorAction =
  (payload?: GetLocatorPayloadType): AppThunk =>
  (dispatch, getState) => {
    const locatorId = getActiveLocator(getState())?.locatorid;

    api.delete(`/locators/${locatorId}`).then(() => {
      dispatch(updateActiveLocator(null));
      dispatch(getLocatorsAction(payload));
    });
  };

export const deleteLocatorLengthAction =
  (payload?: { lengthId: string}): AppThunk =>
  (dispatch, getState) => {
    api.delete('/locators/lengths/' + payload?.lengthId).then(() => {
      dispatch(getLocatorsLengthsAction())
    })
  }

export const deleteLocatorTypeAction =
  (payload?: { typeId: string}): AppThunk =>
  (dispatch, getState) => {
    api.delete('/locators/types/' + payload?.typeId).then(() => {
      dispatch(getLocatorsTypesAction())
    })
  }

export const updateLocatorTypeAction =
  (payload?: {typeId: string, typeName: string}): AppThunk =>
  (dispatch, getState) => {
    api.put('/locators/types/' + payload?.typeId, {typename: payload?.typeName}).then(() => {
      dispatch(getLocatorsTypesAction())
    })
  }


export const getLocatorsAmountAction = (): AppThunk => (dispatch, getState) => {
  const locatorFilters = getLocatorFilters(getState());

  api
    .get(`/locators/total${createUrlParams(locatorFilters)}`)
    .then((total) => dispatch(updateLocatorTotalAmount(total)));
};
